import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import StringUtils from "@/utils/StringUtils";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";
import { RootState } from "@/types/RootState";
import { SearchDataCategory } from "./types";

const SectionLabel = styled.div`
  padding: 8px 12px 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  opacity: 0.5;
  letter-spacing: 0.03em;
`;

const SearchResultItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  background: transparent;
  font-size: 13px;
  font-weight: normal;
  color: #424b59;
  text-decoration: none;

  &:hover {
    background: #f9fafb;
  }
`;

const ItemIcon = styled.div`
  flex-shrink: 0;
  margin-right: 10px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  img,
  svg {
    width: 22px;
    height: 22px;
  }
`;

const ItemSub = styled.div`
  font-size: 11px;
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

interface SearchItemProps {
  dataSearch: SearchDataCategory;
  closePopup: () => void;
  isRecent: boolean;
  strSearch?: string;
}

function SearchItem({
  dataSearch,
  closePopup,
  isRecent,
  strSearch,
}: SearchItemProps) {
  const history = useHistory();
  const loginInfo = useSelector((state: RootState) => state.user);
  const showOrgName =
    loginInfo?.organisation?.organisationType === "SYSTEM" ||
    loginInfo?.organisation?.organisationType === "PARTNER";
  const [showMoreButton, setShowMoreButton] = useState(true);
  const [list, setList] = useState(dataSearch?.listItem.slice(0, 4) || []);
  const [listData] = useState(dataSearch?.listItem || []);
  const [maxList] = useState(dataSearch.listItem.length);
  const [limitList, setLimitList] = useState(8);

  const showMore = () => {
    setList(listData.slice(0, limitList + maxList));
    setLimitList(limitList + maxList);
    if (listData.slice(0, limitList + maxList).length === maxList) {
      setShowMoreButton(false);
    }
  };

  const showLess = () => {
    setList(listData.slice(0, 4));
    setLimitList(4);
    setShowMoreButton(true);
  };

  const sectionLabel = useMemo(() => {
    if (isRecent) return dataSearch.name;
    const categoryKey = `search_category_${dataSearch.name}_name` as LanguageKey;
    const translatedCategory = translate(categoryKey);
    return translatedCategory !== categoryKey ? translatedCategory : dataSearch.name;
  }, [isRecent, dataSearch.name]);

  const getSubtitle = (item: typeof list[0]) => {
    const base = isRecent
      ? item?.category
      : (item?.data.subtitle || item?.data.title);
    const org = showOrgName && item?.data.orgName ? ` · ${item.data.orgName}` : "";
    return base + org;
  };

  const itemList = useMemo(() => {
    return (
      <>
        <SectionLabel>{sectionLabel}</SectionLabel>
        {list.map((item, index) => (
          <SearchResultItem
            key={index}
            onClick={() => {
              history.push(item.link);
              closePopup();
            }}
            title={item?.data.title}
          >
            <ItemIcon>{item?.img}</ItemIcon>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="text-truncate">{item?.data.title}</div>
              <ItemSub>{getSubtitle(item)}</ItemSub>
            </div>
          </SearchResultItem>
        ))}
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSearch, list, sectionLabel]);

  useEffect(() => {
    if (isRecent) {
      setList(dataSearch.listItem);
    } else {
      setList(dataSearch.listItem.slice(0, 4));
    }
  }, [dataSearch, dataSearch.listItem, isRecent]);

  if (isRecent) {
    return <div>{itemList}</div>;
  }

  return (
    <div>
      {itemList}
      {showMoreButton && maxList > 4 && (
        <a
          href="# "
          onClick={(e) => {
            e.preventDefault();
            showMore();
          }}
          style={{
            color: "#5094BE",
            fontSize: 12,
            fontWeight: "normal",
            padding: "4px 12px",
            display: "inline-block",
            textDecoration: "none",
          }}
        >
          {strSearch &&
            StringUtils.replaceStr(
              translate(LanguageKey.v41_view_all_matching_key),
              dataSearch.name.toLowerCase()
            ) +
              ' "' +
              strSearch +
              '"'}
        </a>
      )}
      {!showMoreButton && (
        <a
          href="# "
          onClick={(e) => {
            e.preventDefault();
            showLess();
          }}
          style={{
            color: "#5094BE",
            fontSize: 12,
            fontWeight: "normal",
            padding: "4px 12px",
            display: "inline-block",
            textDecoration: "none",
          }}
        >
          <u>{translate(LanguageKey.v41_show_less_key)}</u>
        </a>
      )}
    </div>
  );
}

export default SearchItem;
